/**
 * @file 参考書ルートタブ — 受験の対象ごとに、参考書を科目 × 難易度の棒と区分ごとの一覧で見る。
 *
 * @what: 表 (既定) は同じ対象のルート (理系編・文系編) を1枚に重ね、本1冊を1行、横軸を偏差値にした
 *   棒で「その本を使う範囲」を出す。目標の志望校を選ぶと縦線が入り、「目標まで届く本が無い切り口」を
 *   上に出す。一覧は対象 → ルート → 科目の順に絞り、区分ごとに推奨の本を表紙つきで並べる。代替・上位・
 *   補助の本は推奨の下に小さく並べる。
 * @why: 参考書ルートは「いまの学習」ではなく先の見取りで、家庭が使うと決めた教材 (教材タブ) と
 *   混ぜない。並びも語彙も出典のものなので、画面は受け取った順のまま出し、この面から
 *   何かを記録することはしない。
 * @failure: 取得失敗は1行で知らせる (backend 未デプロイの間は not_found になる)。詳細は
 *   console のみに出す。
 */

import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  STUDY_TEXTBOOK_ROUTE_ROLE_LABELS,
  groupRoutesByTarget,
  latestTextbookRoutes,
  type StudyTextbookRoute,
  type StudyTextbookRouteBook,
  type StudyTextbookRouteRole,
  type StudyTextbookRouteSchool,
  type StudyTextbookRouteTask,
  type StudyTextbookRouteTrack,
} from "../api/textbookRoutes";
import type { StudySource } from "../api/sources";
import { shelfBooks, type ShelfBooks } from "../model/shelfBooks";
import type { TextbookRoutesState } from "../hooks/useTextbookRoutes";
import {
  axisPercent,
  bookDifficulty,
  coverageGaps,
  mergeRoutesByTarget,
  rangeAxis,
  rangeRows,
  scaleMarkLabel,
  scaleMarkNote,
  scaleNoun,
  supplementBooks,
  type GridSubject,
  type MergedTarget,
  type RangeAxis,
  type RangeBar,
  type TaskBar,
} from "../model/textbookRouteGrid";
import { isbnProductUrl } from "../api/bookLookup";
import { ExamPlanPanel, type ExamPlanStore } from "./ExamPlanPanel";
import type { ActivityByIsbn } from "../model/plannedRanges";
import { SpeakTaskDialog } from "./SpeakTaskDialog";
import { MaterialCover } from "./MaterialsPanel";
import "./study.css";

/** 面の見せ方を表す。棒 (科目 × 難易度) が既定で、区分ごとの一覧とスケジュール (学習者の設定) に切り替えられる。 */
type RouteDisplay = "grid" | "list" | "schedule";

/**
 * 本の表紙を出す (本だなにある本には表紙が左下に印を出す)。
 *
 * @what: 表紙の大きさは外の class (study-route-range-book-cover など) で決める。
 * @why: 棒の面と区分ごとの一覧の3か所で同じ組を使うので、包む入れ物ごと1つにまとめる。
 */
function BookCover({
  book,
  onShelf,
  className,
}: {
  book: StudyTextbookRouteBook;
  onShelf: boolean;
  /** 表紙の大きさを決める class を外から足す。 */
  className?: string;
}) {
  return (
    <span className={className ? `study-route-cover-wrap ${className}` : "study-route-cover-wrap"}>
      <MaterialCover
        isbn={book.isbn}
        url={book.url}
        title={book.title}
        enlargeable
        onShelf={onShelf}
      />
    </span>
  );
}

/** 推奨の下に添える役割の並び順 (代替 → 上位 → 補助)。 */
const SECONDARY_ROLES: readonly StudyTextbookRouteRole[] = [
  "alternative",
  "advanced",
  "supplement",
];

export function TextbookRoutePanel({
  routesState,
  familyId,
  learnerPersonId,
  learnerLabel,
  display: controlledDisplay,
  sources,
  store,
  activity,
}: {
  routesState: TextbookRoutesState;
  /**
   * 外から見せ方を決めるときに渡す (計画の面の切り替え)。grid なら表と区分ごとの一覧の切り替えだけを
   * 中に出し、schedule なら切り替えを出さない。無ければ3つの切り替えを中に出す。
   */
  display?: "grid" | "schedule";
  /** スケジュールの設定を読み書きする家族の id を渡す。 */
  familyId: string;
  /** 選んでいる学習者 (おとなでもよい)。無ければスケジュールは案内だけを出す。 */
  learnerPersonId: string | null;
  learnerLabel: string;
  /** 本だなの資料を渡す。ISBN が一致する本に「本だなにある」の印を出す。読み込み中は null にする。 */
  sources?: readonly StudySource[] | null;
  /** 設定の読み書きを外から渡す。学びの計画の面から渡ると、その計画を読み書きする。 */
  store?: ExamPlanStore;
  /** 本 (ISBN) ごとの触れた日 (スケジュールの表に重ねる印と、今週の実績に使う)。 */
  activity?: ActivityByIsbn;
}) {
  const routes = useMemo(
    () => (routesState.status === "ready" ? latestTextbookRoutes(routesState.routes) : []),
    [routesState],
  );
  const groups = useMemo(() => groupRoutesByTarget(routes), [routes]);

  const merged = useMemo(() => mergeRoutesByTarget(routes), [routes]);
  const shelf = useMemo(() => shelfBooks(familyId, sources), [familyId, sources]);

  const [display, setDisplay] = useState<RouteDisplay>(controlledDisplay ?? "grid");
  // 外から渡された見せ方が変わったら、描画の中で状態を合わせる (Effect を挟むと1回古い面が出る)
  const [prevControlled, setPrevControlled] = useState(controlledDisplay);
  if (controlledDisplay !== prevControlled) {
    setPrevControlled(controlledDisplay);
    if (controlledDisplay !== undefined) setDisplay(controlledDisplay);
  }
  // 課題に口頭で取り組む対話を持つ。学ぶ人を選んでいるときだけ開ける。
  const [speakTask, setSpeakTask] = useState<StudyTextbookRouteTask | null>(null);
  const [targetChoice, setTargetChoice] = useState<string | null>(null);
  const [routeChoice, setRouteChoice] = useState<string | null>(null);
  const [subjectChoice, setSubjectChoice] = useState<string | null>(null);

  // 選択が無い・無効なら先頭に倒す (取り込み直しで id が変わっても面が空にならないようにする)。
  const group = groups.find((g) => g.target === targetChoice) ?? groups[0];
  const route = group?.routes.find((r) => r.id === routeChoice) ?? group?.routes[0];
  const subject = route?.subjects.find((s) => s.id === subjectChoice) ?? route?.subjects[0];
  const mergedTarget = merged.find((m) => m.target === group?.target);
  // 目盛りの値から名前を引けるようにする。課題の評価で「届いた段」を名前で言うのに使う。
  const markLabels = useMemo(
    () => new Map((mergedTarget?.scaleMarks ?? []).map((m) => [m.value, m.label])),
    [mergedTarget],
  );

  return (
    <section className="study-section">
      <h2 className="study-section-title">
        <span aria-hidden="true">🧭</span> 参考書ルート
      </h2>
      <p className="study-section-help">
        受験の対象ごとに、どの参考書をどの順に使うかの見取りです。いま使う教材は「教材」に
        登録し、ここは先の計画を立てるときに見ます。
      </p>
      {speakTask !== null && learnerPersonId !== null ? (
        <SpeakTaskDialog
          task={speakTask}
          markLabels={markLabels}
          familyId={familyId}
          learnerPersonId={learnerPersonId}
          onClose={() => setSpeakTask(null)}
        />
      ) : null}
      {routesState.status === "loading" ? (
        // 読み込みの間も高さを取っておく (表が入るとページが 600px ほど伸びて跳ねる。実測、2026-09-08)
        <p role="status" className="study-muted study-panel-loading">
          参考書ルートを読み込んでいます...
        </p>
      ) : routesState.status === "error" ? (
        <p role="alert" className="study-error">
          参考書ルートを読み込めませんでした。時間を置いて再読み込みしてください。
        </p>
      ) : !group || !route || !subject ? (
        <p className="study-muted">
          まだ参考書ルートを取り込んでいません。取り込むと、科目ごとの参考書がここに並びます。
        </p>
      ) : (
        <>
          {groups.length > 1 ? (
            <div className="study-reading-levels" role="tablist" aria-label="受験の対象">
              {groups.map((g) => (
                <button
                  key={g.target}
                  type="button"
                  role="tab"
                  aria-selected={g.target === group.target}
                  className="study-reading-level-tab"
                  onClick={() => {
                    setTargetChoice(g.target);
                    setRouteChoice(null);
                    setSubjectChoice(null);
                  }}
                >
                  <span className="study-reading-level-id">{g.target}</span>
                </button>
              ))}
            </div>
          ) : (
            <h3 className="study-route-target">{group.target}</h3>
          )}
          {controlledDisplay !== "schedule" ? (
            <div className="study-route-display" role="group" aria-label="見せ方">
              <button
                type="button"
                className="study-ghost-btn"
                aria-pressed={display === "grid"}
                onClick={() => setDisplay("grid")}
              >
                科目 × 難易度
              </button>
              <button
                type="button"
                className="study-ghost-btn"
                aria-pressed={display === "list"}
                onClick={() => setDisplay("list")}
              >
                区分ごとの一覧
              </button>
              {controlledDisplay === undefined ? (
                <button
                  type="button"
                  className="study-ghost-btn"
                  aria-pressed={display === "schedule"}
                  onClick={() => setDisplay("schedule")}
                >
                  スケジュール
                </button>
              ) : null}
            </div>
          ) : null}
          {display === "schedule" && mergedTarget ? (
            <ExamPlanPanel
              familyId={familyId}
              learnerPersonId={learnerPersonId}
              learnerLabel={learnerLabel}
              merged={mergedTarget}
              shelf={shelf}
              {...(store ? { store } : {})}
              {...(activity ? { activity } : {})}
            />
          ) : null}
          {display === "grid" && mergedTarget ? (
            <>
              {group.routes.map((r) => (
                <RouteCredit key={r.id} route={r} />
              ))}
              <RouteRangeChart merged={mergedTarget} shelf={shelf} />
            </>
          ) : null}
          {display === "list" && group.routes.length > 1 ? (
            <div className="study-reading-levels" role="tablist" aria-label="ルート">
              {group.routes.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  role="tab"
                  aria-selected={r.id === route.id}
                  className="study-reading-level-tab"
                  onClick={() => {
                    setRouteChoice(r.id);
                    setSubjectChoice(null);
                  }}
                >
                  <span className="study-reading-level-id">{r.title}</span>
                </button>
              ))}
            </div>
          ) : null}
          {display === "list" ? (
            <>
              <RouteCredit route={route} />
              <div className="study-reading-levels" role="tablist" aria-label="科目">
                {route.subjects.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="tab"
                    aria-selected={s.id === subject.id}
                    className="study-reading-level-tab"
                    onClick={() => setSubjectChoice(s.id)}
                  >
                    <span className="study-reading-level-id">{s.label}</span>
                    <span className="study-reading-level-hint">{s.tracks.length}区分</span>
                  </button>
                ))}
              </div>
              <div className="study-route-tracks" aria-label={`${subject.label}の区分`}>
                {subject.tracks.map((t) => (
                  <TrackCard
                    key={t.id}
                    track={t}
                    shelf={shelf}
                    {...(learnerPersonId !== null ? { speak: setSpeakTask } : {})}
                  />
                ))}
              </div>
            </>
          ) : null}
        </>
      )}
    </section>
  );
}

/** ルートの名前・改訂版・出典・目盛りを1行で出す。 */
function RouteCredit({ route }: { route: StudyTextbookRoute }) {
  return (
    <p className="study-section-help">
      {route.title}
      {route.edition ? `（${route.edition}）` : ""} 出典 {route.source_name}
      {route.source_url ? (
        <>
          {" "}
          <a href={route.source_url} target="_blank" rel="noreferrer">
            原本を見る
          </a>
        </>
      ) : null}
      {route.scale ? `。到達の目安は${route.scale}です。` : null}
    </p>
  );
}

/** 区分1つ (単語帳・文法書など) のカード。推奨を大きく、他の役割は下に小さく添える。 */
function TrackCard({
  track,
  shelf,
  speak,
}: {
  track: StudyTextbookRouteTrack;
  shelf: ShelfBooks;
  /** 課題に口頭で取り組むときに呼ぶ。学ぶ人を選んでいないときは渡らない。 */
  speak?: (task: StudyTextbookRouteTask) => void;
}) {
  const primaries = track.books.filter((b) => b.role === "primary");
  return (
    <article className="study-route-track">
      <h4 className="study-route-track-title">{track.label}</h4>
      {track.note ? <p className="study-route-track-note">{track.note}</p> : null}
      {(track.tasks ?? []).length > 0 ? (
        <ul className="study-route-tasks" aria-label={`${track.label}の課題`}>
          {(track.tasks ?? []).map((k) => (
            <li key={k.id} className="study-route-task">
              <div className="study-route-task-head">
                <span className="study-route-task-badge" aria-hidden="true">
                  課題
                </span>
                <b>{k.label}</b>
                {k.minutes ? (
                  <span className="study-muted">1回{k.minutes}分</span>
                ) : null}
              </div>
              <p className="study-route-task-scene">{k.scene}</p>
              {/* 相手役の一言はここに出さない。先に読めると聞き取りの練習にならないためである
                  (設計 plan/docs/study_skill_task_design.md §5.6)。 */}
              <p className="study-route-task-role">相手役: {k.role}</p>
              {speak !== undefined ? (
                <button type="button" className="study-ghost-btn" onClick={() => speak(k)}>
                  話して返す
                </button>
              ) : null}
              <ul className="study-route-task-criteria">
                {k.criteria.map((c) => (
                  <li key={c.mark}>
                    <span className="study-route-task-mark">{c.mark}</span>
                    {c.must}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      ) : null}
      {primaries.length > 0 ? (
        <ul className="study-route-books" aria-label={`${track.label}のおすすめ`}>
          {primaries.map((b, i) => (
            <li key={`${b.title}-${i}`} className="study-route-book">
              <BookCover book={b} onShelf={shelf.has(b.isbn)} />
              <BookBody book={b} shelfHref={shelf.href(b.isbn)} />
            </li>
          ))}
        </ul>
      ) : null}
      {SECONDARY_ROLES.map((role) => {
        const books = track.books.filter((b) => b.role === role);
        if (books.length === 0) return null;
        return (
          <div key={role} className="study-route-secondary">
            <span className="study-route-role">{STUDY_TEXTBOOK_ROUTE_ROLE_LABELS[role]}</span>
            <ul
              className="study-route-books study-route-books--compact"
              aria-label={`${track.label}の${STUDY_TEXTBOOK_ROUTE_ROLE_LABELS[role]}`}
            >
              {books.map((b, i) => (
                <li key={`${b.title}-${i}`} className="study-route-book study-route-book--compact">
                  <BookCover book={b} onShelf={shelf.has(b.isbn)} />
                  <BookBody book={b} shelfHref={shelf.href(b.isbn)} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </article>
  );
}

/**
 * 書名を出す (持っている本は本だなの読む面へ、それ以外は商品ページか出典へ)。
 *
 * @why: すでに持っている本を商品ページへ送っても、買う先しか出てこない。その場で読める面へ
 *   送る (代表の要望、2026-09-08)。棒の面と区分ごとの一覧で同じ決め方を使う。
 */
function BookTitleLink({
  book,
  shelfHref,
}: {
  book: StudyTextbookRouteBook;
  shelfHref?: string | undefined;
}) {
  if (shelfHref !== undefined) {
    return (
      <Link to={shelfHref} aria-label={`${book.title} を本だなで読む`}>
        {book.title}
      </Link>
    );
  }
  // 商品ページの URL を持たない本は ISBN から引く。※なぜなら持っていない本を買いに行けないと、
  // カリキュラムを見ても次に手に入れるものが分からないためである。
  const link = book.url ?? book.source_url ?? isbnProductUrl(book.isbn);
  if (!link) return <>{book.title}</>;
  return (
    <a href={link} target="_blank" rel="noreferrer">
      {book.title}
    </a>
  );
}

/** 書名・出版社・注記・習得レベルごとの到達の目安を出す。 */
function BookBody({
  book,
  shelfHref,
}: {
  book: StudyTextbookRouteBook;
  /** 本だなにこの本があるときの、読む面への道を渡す。 */
  shelfHref?: string | undefined;
}) {
  const diff = bookDifficulty(book);
  return (
    <div className="study-route-book-main">
      <div className="study-route-book-title">
        <BookTitleLink book={book} shelfHref={shelfHref} />
      </div>
      {book.publisher || book.note ? (
        <div className="study-reading-book-meta">
          {book.publisher ? (
            <span className="study-reading-book-publisher">{book.publisher}</span>
          ) : null}
          {book.note ? <span className="study-reading-book-note">{book.note}</span> : null}
        </div>
      ) : null}
      {diff ? (
        <p className="study-route-difficulty">
          <span className="study-route-difficulty-value">
            水準 {diff.to > diff.from ? `${diff.from}〜${diff.to}` : diff.from}
          </span>
          {diff.source ? (
            <span className="study-route-difficulty-source">出どころ: {diff.source}</span>
          ) : null}
        </p>
      ) : null}
      {book.levels.length > 0 ? (
        <ul className="study-route-levels" aria-label={`${book.title} の習得レベルと到達の目安`}>
          {book.levels.map((l, i) => (
            <li key={`${l.label}-${i}`} className="study-route-level">
              <span className="study-route-level-label">{l.label}</span>
              {l.target ? <span className="study-route-level-target">{l.target}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {book.url && book.source_url ? (
        <a
          className="study-route-book-source"
          href={book.source_url}
          target="_blank"
          rel="noreferrer"
        >
          出典の解説を見る
        </a>
      ) : null}
    </div>
  );
}

/** 目標 (志望校) の選択に使う値を作る。空文字は未選択を表す。 */
function schoolKey(name: string): string {
  return `school:${name}`;
}

/** 志望校のラベルを、横に重ならないように何段かへ振り分ける。 */
function schoolLanes(
  axis: RangeAxis,
  schools: readonly StudyTextbookRouteSchool[],
): { school: StudyTextbookRouteSchool; percent: number; lane: number }[] {
  // 1文字あたりのおよその幅を物差しに対する百分率へ直し、隣と重なる分だけ段を下げる
  const span = axis.max - axis.min;
  const out: { school: StudyTextbookRouteSchool; percent: number; lane: number }[] = [];
  const laneRight: number[] = [];
  for (const s of [...schools].sort((a, b) => a.deviation - b.deviation)) {
    const width = ((s.name.length * 0.75 + 0.5) / span) * 100;
    const left = Math.min(Math.max(axisPercent(axis, s.deviation) - width / 2, 0), 100 - width);
    let lane = 0;
    while (laneRight[lane] !== undefined && (laneRight[lane] as number) > left - 0.5) lane += 1;
    laneRight[lane] = left + width;
    out.push({ school: s, percent: left, lane });
  }
  return out;
}

/**
 * 科目 × 難易度の面を出す。
 *
 * @what: 本1冊を1行にし、横軸を偏差値にした棒で「その本を使う範囲」を出す。濃い棒が使う範囲
 *   (使い始める値から使い終える値まで)、同じ位置から伸びる薄い棒が、出典の基準まで仕上げたときに
 *   届くとされる範囲になる。目標の志望校を選ぶと縦線が入り、そこまで届く本が無い切り口を上に出す。
 * @why: 本は点ではなく範囲なので、偏差値を行にした表に置くと1冊が何行にも現れて読めなくなった
 *   (代表の指摘、2026-09-06)。棒にすると1冊が1回だけ現れ、範囲が長さで読める。
 * @failure: 偏差値の数値をまったく持たない本は棒にできないので、切り口の下に名前だけを出す。
 */
function RouteRangeChart({ merged, shelf }: { merged: MergedTarget; shelf: ShelfBooks }) {
  const axis = useMemo(() => rangeAxis(merged), [merged]);
  const supplements = useMemo(() => supplementBooks(merged), [merged]);
  const [goal, setGoal] = useState<string>("");

  const goalSchool = merged.schools.find((sc) => schoolKey(sc.name) === goal) ?? null;
  const gaps = useMemo(
    () => (goalSchool ? coverageGaps(merged, goalSchool.deviation) : []),
    [merged, goalSchool],
  );
  const lanes = useMemo(
    () => (axis ? schoolLanes(axis, merged.schools) : []),
    [axis, merged.schools],
  );

  if (merged.subjects.length === 0) return null;
  if (!axis)
    return (
      <p className="study-muted">
        このルートには{scaleNoun(merged)}の目安が登録されていないので、棒に出せません。区分ごとの
        一覧で見られます。
      </p>
    );
  const laneCount = lanes.reduce((a, l) => Math.max(a, l.lane + 1), 0);
  return (
    <div className="study-route-range">
      <p className="study-section-help">
        {merged.routeTitles.join("・")}を1枚に重ねています。濃い棒はその本を使う
        {merged.scale ?? "難易度"}の範囲で、同じところから伸びる薄い棒は、出典が定めた基準まで
        仕上げたときに届くとされる範囲です。書名と棒の左端に表紙が付きます。
      </p>
      {merged.schools.length > 0 ? (
        <label className="study-route-goal">
          <span>{merged.scaleMarks.length > 0 ? "目標" : "目標の志望校"}</span>
          <select value={goal} onChange={(e) => setGoal(e.target.value)}>
            <option value="">選ばない</option>
            {merged.schools.map((sc) => (
              <option key={sc.name} value={schoolKey(sc.name)}>
                {sc.name}（{scaleMarkLabel(merged, sc.deviation)}）
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {goalSchool ? (
        gaps.length > 0 ? (
          <ul className="study-route-gaps" aria-label="目標まで届く本が無い切り口">
            {gaps.map((g) => (
              <li key={g.subjectId}>
                <b>{g.subjectLabel}</b>: {g.tracks.map((t) => t.label).join("・")}に、
                {scaleMarkLabel(merged, goalSchool.deviation)}
                まで届く本がありません
              </li>
            ))}
          </ul>
        ) : (
          <p className="study-muted" role="status">
            どの科目の切り口にも、{scaleMarkLabel(merged, goalSchool.deviation)}
            まで届く本があります。
          </p>
        )
      ) : null}
      <div className="study-route-range-scroll">
        <table className="study-route-range-table">
          <thead>
            <tr>
              <th scope="col" className="study-route-range-corner">
                {merged.scale ?? "難易度"}
              </th>
              <th scope="col" className="study-route-range-axis">
                <span
                  className="study-route-range-axis-inner"
                  style={{ height: `${20 + laneCount * 15}px` }}
                >
                  {axis.ticks
                    .filter((t) => t.major)
                    .map((t) => (
                      <span
                        key={t.value}
                        className="study-route-range-tick"
                        style={{
                          left: `${axisPercent(axis, t.value)}%`,
                          // 両端の目盛りは中央寄せにすると枠の外へ出るので、内側へ寄せる
                          transform:
                            t.value === axis.min
                              ? "none"
                              : t.value === axis.max
                                ? "translateX(-100%)"
                                : "translateX(-50%)",
                        }}
                        title={scaleMarkNote(merged, t.value)}
                      >
                        {t.label ?? t.value}
                      </span>
                    ))}
                  {lanes.map((l) => (
                    <span
                      key={l.school.name}
                      className={
                        goalSchool?.name === l.school.name
                          ? "study-route-range-school study-route-range-school--goal"
                          : "study-route-range-school"
                      }
                      style={{ left: `${l.percent}%`, top: `${16 + l.lane * 15}px` }}
                      title={l.school.note ?? undefined}
                    >
                      {l.school.name}
                    </span>
                  ))}
                </span>
              </th>
            </tr>
          </thead>
          {merged.subjects.map((s) => (
            <SubjectRows
              key={s.id}
              subject={s}
              axis={axis}
              goalDeviation={goalSchool?.deviation ?? null}
              shelf={shelf}
              scaleNoun={scaleNoun(merged)}
            />
          ))}
        </table>
      </div>
      <p className="study-route-range-legend">
        <span className="study-route-range-legend-bar" aria-hidden="true" />
        使う範囲
        <span
          className="study-route-range-legend-bar study-route-range-legend-bar--reach"
          aria-hidden="true"
        />
        仕上げると届く範囲
      </p>
      {supplements.length > 0 ? (
        <p className="study-muted">
          出典が用意する補助教材{supplements.length}
          冊は棒に載せていません。区分ごとの一覧で見られます。
        </p>
      ) : null}
    </div>
  );
}


/** 科目1つぶんの行 (科目の見出し → 切り口 → 本) を出す。 */
function SubjectRows({
  subject,
  axis,
  goalDeviation,
  shelf,
  scaleNoun,
}: {
  subject: GridSubject;
  axis: RangeAxis;
  goalDeviation: number | null;
  shelf: ShelfBooks;
  /** 物差しの名前で、文言に差し込む (偏差値・試験の区分・段)。 */
  scaleNoun: string;
}) {
  const rows = useMemo(() => rangeRows(subject), [subject]);
  return (
    <tbody className="study-route-range-subject">
      <tr>
        <th scope="rowgroup" className="study-route-range-subject-head">
          {subject.label}
        </th>
        <td className="study-route-range-cell">
          <GridLines axis={axis} goalDeviation={goalDeviation} />
        </td>
      </tr>
      {rows.map((row) => (
        <Fragment key={row.track.id}>
          <tr>
            <th scope="row" className="study-route-range-track-head">
              <span className="study-route-range-track-label">{row.track.label}</span>
              {row.unrated.length > 0 ? (
                <span className="study-route-range-unrated">
                  {row.unrated.map((gb) => gb.book.title).join("、")}は
                  {scaleNoun}の目安がありません
                </span>
              ) : null}
            </th>
            <td className="study-route-range-cell">
              <GridLines axis={axis} goalDeviation={goalDeviation} />
            </td>
          </tr>
          {row.bars.map((bar) => (
            <BarRow
              key={bar.book.key}
              bar={bar}
              axis={axis}
              goalDeviation={goalDeviation}
              shelfHref={shelf.href(bar.book.book.isbn)}
            />
          ))}
          {row.taskBars.map((bar) => (
            <TaskBarRow key={bar.task.key} bar={bar} axis={axis} goalDeviation={goalDeviation} />
          ))}
        </Fragment>
      ))}
    </tbody>
  );
}

/**
 * 課題1件の行を出す。
 *
 * @what: 本の棒と同じ軸に置き、印で課題だと分かるようにする。触れると場面が出る。
 * @why: 課題は本と同じ物差しの上にあるので、同じ表で並べて読めるほうがよい。本と混ざらないように
 *   色と印を変える (技能のカリキュラムは本と課題の混成になる)。
 */
function TaskBarRow({
  bar,
  axis,
  goalDeviation,
}: {
  bar: TaskBar;
  axis: RangeAxis;
  goalDeviation: number | null;
}) {
  const left = axisPercent(axis, bar.from);
  const width = Math.max(axisPercent(axis, bar.to) - left, 1.2);
  const k = bar.task.task;
  return (
    <tr className="study-route-range-task-row">
      <th scope="row" className="study-route-range-book-head">
        <span className="study-route-task-badge" aria-hidden="true">
          課題
        </span>
        <span className="study-route-range-book-title">{k.label}</span>
      </th>
      <td className="study-route-range-cell">
        <GridLines axis={axis} goalDeviation={goalDeviation} />
        <span className="study-route-range-bars">
          <span
            className="study-route-range-bar study-route-range-bar--task"
            style={{ left: `${left}%`, width: `${width}%` }}
            title={`${bar.from}〜${bar.to}で取り組む（${k.scene}）`}
          />
        </span>
      </td>
    </tr>
  );
}

/** 目盛りの縦線と、目標の志望校の縦線を出す。 */
function GridLines({ axis, goalDeviation }: { axis: RangeAxis; goalDeviation: number | null }) {
  return (
    <span className="study-route-range-lines" aria-hidden="true">
      {axis.ticks.map((t) => (
        <span
          key={t.value}
          className={
            t.major
              ? "study-route-range-line study-route-range-line--major"
              : "study-route-range-line"
          }
          style={{ left: `${axisPercent(axis, t.value)}%` }}
        />
      ))}
      {goalDeviation !== null ? (
        <span
          className="study-route-range-goal-line"
          style={{ left: `${axisPercent(axis, goalDeviation)}%` }}
        />
      ) : null}
    </span>
  );
}

/**
 * 棒がこの位置より左から始まる行では、表紙の左に印を置けないとみなす。
 *
 * @why: 印は 9px なので、表紙の左に置くには目盛りの幅の数 % が必要である。目盛りの下限は5の
 *   倍数に丸めるので、いちばん低い本がちょうど下限に当たると棒が左端から始まる
 *   (代表の指摘、2026-09-08)。
 */
const FLUSH_LEFT_PERCENT = 4;

/** 本1冊の行 (書名と棒) を出す。 */
function BarRow({
  bar,
  axis,
  goalDeviation,
  shelfHref,
}: {
  bar: RangeBar;
  axis: RangeAxis;
  goalDeviation: number | null;
  /** 本だなにこの本があるときの、読む面への道を渡す。表紙の左下の印もこれで決まる。 */
  shelfHref?: string | undefined;
}) {
  const b = bar.book.book;
  const left = axisPercent(axis, bar.from);
  const useWidth = Math.max(axisPercent(axis, bar.to) - left, 1.2);
  const reachWidth = bar.reach !== null ? axisPercent(axis, bar.reach) - left : 0;
  const rangeLabel = bar.to > bar.from ? `${bar.from}〜${bar.to}で使う` : `${bar.from}で使う`;
  const reachLabel =
    bar.reach !== null
      ? `仕上げると${bar.reach}（${bar.steps.map((st) => `${st.label} → ${st.deviation}`).join("、")}）`
      : "";
  return (
    <tr className={b.role === "primary" ? undefined : "study-route-range-row--secondary"}>
      <th scope="row" className="study-route-range-book-head">
        <span className="study-route-range-book-inner">
          <BookCover
            book={b}
            onShelf={shelfHref !== undefined}
            className="study-route-range-book-cover"
          />
          <span className="study-route-range-book-title">
            <BookTitleLink book={b} shelfHref={shelfHref} />
            {b.role !== "primary" ? (
              <span className="study-route-range-role">
                （{STUDY_TEXTBOOK_ROUTE_ROLE_LABELS[b.role]}）
              </span>
            ) : null}
          </span>
        </span>
      </th>
      <td className="study-route-range-cell">
        <GridLines axis={axis} goalDeviation={goalDeviation} />
        <span className="study-route-range-bars">
          {bar.reach !== null ? (
            <span
              className="study-route-range-bar study-route-range-bar--reach"
              style={{ left: `${left}%`, width: `${reachWidth}%` }}
              title={reachLabel}
            />
          ) : null}
          <span
            className={[
              "study-route-range-bar",
              bar.fromReach ? "study-route-range-bar--estimated" : "",
              // 本だなにある本は、棒を緑の枠で囲んで持っていることを表の中で見分けられるようにする
              shelfHref !== undefined ? "study-route-range-bar--owned" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{ left: `${left}%`, width: `${useWidth}%` }}
            title={bar.source ? `${rangeLabel}（${bar.source}）` : rangeLabel}
          />
          <span
            className={
              // 棒が目盛りの左端から始まる行は、表紙の左に印を置く場所が無いので右へ回す
              left < FLUSH_LEFT_PERCENT
                ? "study-route-range-bar-cover study-route-range-bar-cover--flush"
                : "study-route-range-bar-cover"
            }
            style={{ left: `${left}%` }}
          >
            {/* 表紙が 17px と小さいので、印は重ねずに表紙の左隣へ置く (棒の始まりより左は空いている) */}
            <MaterialCover
              isbn={b.isbn}
              url={b.url}
              title={b.title}
              onShelf={shelfHref !== undefined}
            />
          </span>
        </span>
        <span className="study-visually-hidden">
          {rangeLabel}
          {reachLabel ? `。${reachLabel}` : ""}
        </span>
      </td>
    </tr>
  );
}
